import { useState, useEffect, useMemo, useRef, useCallback, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  normalizeDiscoveryRequest,
  DISCOVERY_PROTOCOL_VERSION,
  COMUNE_PRESET_RADIUS_KM,
  activeRadiusKm,
  territoryRequestOf,
  type TerritoryCapabilities,
  type DiscoveryNormalization,
  type DiscoveryDescriptor,
  type LocationChoice,
  type TerritoryResolution,
  type RegistryCityItem,
  type ResolvedMunicipality,
  type GeographicHint,
} from '@buzzmap/shared';
import {
  DiscoveryContext,
  buildDiscoveryRequestHeaders,
  publishDiscoveryMode,
  type DiscoveryContextType,
  type DiscoveryHintStatus,
  type DiscoveryMode,
  type DiscoveryStatus,
  type DiscoveryUpgradeReason,
} from './discoveryStore';
import { useUserContext } from './UserContext';
import { usePrimaryRoutesLocation } from '../routes/usePrimaryRoutesLocation';
import {
  findRegistryById,
  loadRegistry,
  matchRegistryPair,
  resetRegistryCache,
} from '../utils/registryLookup';
import { resolvePoint } from '../utils/gpsPlacement';
import { composeTerritoryTarget } from '../utils/discoveryFilters';
import { purgePublicApiSnapshots } from '../lib/swControl';
import {
  consumeSsrStart,
  discardSsrBootstrap,
  peekSsrCaps,
  peekSsrTerritory,
} from '../utils/ssrBootstrap';
import { HintSelectionPrompt } from '../components/discovery/HintSelectionPrompt';
import { DiscoveryEpochNotice } from '../components/discovery/DiscoveryEpochNotice';
import { RouteMetadata } from '../components/discovery/RouteMetadata';

export { useDiscovery, useDiscoveryOptional } from './discoveryStore';
export type {
  DiscoveryContextType,
  DiscoveryMode,
  DiscoveryStatus,
  DiscoveryHintStatus,
} from './discoveryStore';

/**
 * Route-owned discovery state (T010-FE, canonical territories plan).
 *
 * One public `DiscoveryProvider` owns the normalized primary URL, the resolved
 * registry context and the pending/ready/invalid/unavailable/upgrade-required
 * status. It sits BELOW `UserProvider` (so it can read the saved intent for the
 * central generic-entry replacement) and ABOVE `EventsProvider` (so the feed can
 * read the active location and the no-nationwide-interim-fetch gate).
 *
 * RELEASE GATING: the backend's new grammar ships behind the `navigation-v2`
 * switch and the registry `ready` flag (both OFF until T018). The client gates
 * its v2 behaviour on `GET /api/territory?mode=capabilities` so the SAME
 * deployed bundle behaves exactly as today until activation:
 * - bootstrap in flight / `ready === false` / a capabilities fault → `legacy`
 *   mode: the pre-existing `locationOverride` + `useTerritoryRouteResolution`
 *   machinery stays fully active and this provider is inert.
 * - `ready === true` and a compatible protocol → `v2` mode: the normalized URL
 *   is the sole active authority, generic localized entries are replaced
 *   centrally (including legacy `?city=`/`centers` hints), and territory
 *   resolution is cached per (registryVersion, path) with the epoch metadata
 *   declared on every request.
 * - `ready === true` but the server demands a higher protocol →
 *   `upgrade-required`: T016 renders the explicit upgrade state; the client
 *   never silently reactivates old GPS/generic-localized behaviour.
 *
 * W1 (CLOSED in T016-FE): a capabilities error/hang degrades to `legacy` only
 * while the epoch has never been seen active (the compatible window). Once
 * activation is known — `ready:true` observed, or any discovery request was
 * answered with HTTP 426 — the same fault becomes `upgrade-required` with
 * reason `maintenance`: an explicit blocking state, never legacy. Renegotiation
 * runs at startup, on returning visible/online and on every 426 (one bounded
 * attempt; a second 426 confirms the state until retry/reload).
 *
 * The legacy mode is the compatibility bridge; T011–T016 migrate consumers off
 * the old `locationOverride`/GPS paths and retire it. All parsing/normalization
 * comes from `@buzzmap/shared` (T003) — the codec is never duplicated here.
 */

/** Bounded bootstrap so a hung capabilities request cannot stall the gate (W1). */
const CAPS_TIMEOUT_MS = 8000;

/** Minimum gap between spontaneous renegotiations (tab visible / online). */
const RENEGOTIATE_THROTTLE_MS = 5000;

/** Limite delle risoluzioni in memoria per provider, con espulsione LRU. */
const RESOLUTION_CACHE_LIMIT = 64;

type CapsStatus = 'loading' | 'loaded' | 'error';

/** Outcome of the territory resolution fetch: an HTTP error status, or a parsed body. */
type ResolutionOutcome = { kind: 'http'; status: number } | { kind: 'ok'; body: unknown };

/** Mode + reason in one pass so the two can never drift apart. */
type DiscoveryGate = { mode: DiscoveryMode; upgradeReason: DiscoveryUpgradeReason | null };

/**
 * Mode table (T010-FE §1, extended by T016-FE's update-aware bootstrap):
 *
 * - `loading` — first bootstrap in flight (no capabilities yet). The one
 *   exception: an OFFLINE cold start never bootstraps, so it resolves
 *   `legacy` directly (the offline arm below, T018 review W-1) instead of
 *   holding here forever.
 * - `legacy` — `ready:false` or a bootstrap fault BEFORE the epoch was ever
 *   seen active: the compatible window, behaviour identical to HEAD.
 * - `upgrade-required` — the epoch is known active and either the server
 *   demands a higher protocol, a discovery request was rejected with 426 even
 *   after renegotiation (`upgradeConfirmed`), or the capabilities bootstrap
 *   fails / reports `ready:false` after activation (`maintenance`, plan l.71,
 *   T010 W1 — audit H1: a rollback or buggy server must never reactivate
 *   legacy GPS/generic-localized behaviour). NEVER degrades back to legacy
 *   once activation is known.
 * - `v2` — negotiated, URL is the sole authority.
 *
 * A re-bootstrap (tab visible/online, 426, retry) keeps serving the LAST GOOD
 * capabilities until new ones land, so a transient blip can neither demote a
 * live v2 session to legacy nor flash the maintenance state.
 *
 * The reason is `maintenance` exactly when `upgrade-required` came from an
 * unreachable/regressed reference (fault or `ready:false` after activation),
 * `protocol` otherwise; it is null in every other mode.
 */
function computeGate(
  caps: TerritoryCapabilities | null,
  capsStatus: CapsStatus,
  activationSeen: boolean,
  upgradeConfirmed: boolean,
  online: boolean
): DiscoveryGate {
  if (upgradeConfirmed) return { mode: 'upgrade-required', upgradeReason: 'protocol' };
  if (capsStatus === 'loading') {
    // T018 review W-1 — offline COLD START: the bootstrap effect below skips
    // the fetch while the browser is offline, so nothing would ever resolve
    // this gate — every consumer (feed, galleries, map) would hold at
    // `loading` for the whole offline session, whereas production HEAD fired
    // the requests immediately and the service worker's api-cache painted the
    // cached cards. Resolve the first-load offline case to the compatible
    // `legacy` window instead: the HEAD-shaped requests go out and the SW can
    // serve them. Guarded by `!activationSeen` so the T016 rule stands —
    // activation is session-scoped (never persisted), so a COLD start can
    // never be post-activation, and a mid-session 426 followed by an offline
    // re-bootstrap keeps the hold (fail-closed) until the `online` event
    // renegotiates. Online paths are unchanged: bounded by CAPS_TIMEOUT_MS,
    // then the error arm below resolves legacy (pre-activation) or
    // maintenance (post-activation).
    //
    // Audit M2: `online` is REACTIVE state (the listeners below keep it in
    // step with the browser), not an ambient `navigator.onLine` read — a
    // non-reactive read went stale the moment the browser reconnected while
    // this bootstrap was still in flight, so the memo kept answering `legacy`
    // for a whole capabilities round-trip and the feed could place raw GPS
    // centers in a request URL. Offline is the only world where `legacy` is
    // ever returned from this arm.
    if (!activationSeen && !online) {
      return { mode: 'legacy', upgradeReason: null };
    }
    return { mode: 'loading', upgradeReason: null };
  }
  // A bootstrap fault (or a loaded `ready:false`, audit H1) is the compatible
  // window before activation, and the explicit maintenance block after it.
  if (capsStatus === 'error' || !caps || !caps.ready) {
    return activationSeen
      ? { mode: 'upgrade-required', upgradeReason: 'maintenance' }
      : { mode: 'legacy', upgradeReason: null };
  }
  if (DISCOVERY_PROTOCOL_VERSION < caps.minimumDiscoveryProtocol) {
    return { mode: 'upgrade-required', upgradeReason: 'protocol' };
  }
  return { mode: 'v2', upgradeReason: null };
}

/** Runtime guard for the capabilities body (S4) — a proxy/error-page JSON must not slip through. */
function isTerritoryCapabilities(v: unknown): v is TerritoryCapabilities {
  if (!v || typeof v !== 'object') return false;
  const o = v as Record<string, unknown>;
  return (
    typeof o.ready === 'boolean' &&
    typeof o.activeRegistryVersion === 'string' &&
    typeof o.minimumDiscoveryProtocol === 'number' &&
    Number.isFinite(o.minimumDiscoveryProtocol)
  );
}

/**
 * T204-INT: capabilities the SSR document was built with, usable as the
 * "last good" capabilities of the first render. Only when they describe an
 * activated epoch this bundle speaks, and the browser is online (an offline
 * cold start keeps its `legacy` window so the service worker can serve the
 * cached API). Anything else: undefined, the bootstrap of today.
 */
function embeddedStart(): TerritoryCapabilities | undefined {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return undefined;
  const embedded = peekSsrCaps();
  if (!embedded || !embedded.ready) return undefined;
  if (DISCOVERY_PROTOCOL_VERSION < embedded.minimumDiscoveryProtocol) return undefined;
  return embedded;
}

/** Runtime guard for a resolution body (S4). */
function isTerritoryResolution(v: unknown): v is TerritoryResolution {
  if (!v || typeof v !== 'object') return false;
  const o = v as Record<string, unknown>;
  return (
    typeof o.canonicalPath === 'string' &&
    typeof o.scope === 'string' &&
    !!o.location &&
    typeof o.location === 'object'
  );
}

function toResolvedMunicipality(c: RegistryCityItem): ResolvedMunicipality {
  return {
    id: c.id,
    istatCode: c.istatCode,
    name: c.name,
    province: c.province,
    provinceName: c.provinceName,
    region: c.region,
    path: c.path,
    center: { lat: c.lat, lng: c.lng },
  };
}

/**
 * True when the descriptor carries non-geographic URL state — categories, search,
 * dates, an explicit map view, or a free-map bbox (B-1, plan l.60/l.62/l.65-66).
 */
function hasNonGeoState(d: DiscoveryDescriptor): boolean {
  return (
    d.categoryIds.length > 0 ||
    d.filters.search !== undefined ||
    d.filters.startDate !== undefined ||
    d.filters.endDate !== undefined ||
    d.view === 'map' ||
    d.mapBbox !== null
  );
}

export function DiscoveryProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  // Saved intent is the v2 record (T011-FE): `null` until the preference owner
  // has hydrated/converted its v2 store — see the entry effect's hold.
  const { preferencesV2 } = useUserContext();
  const primary = usePrimaryRoutesLocation();
  // Raw foreground location. The provider's replace-navigations must never move
  // a foreground `/event/:id` or auth overlay (plan l.76, B3): they only run
  // when the foreground IS the route being normalized.
  const raw = useLocation();
  const foregroundIsPrimary = raw.pathname === primary.pathname && raw.search === primary.search;

  // --- capabilities bootstrap (the epoch gate; bounded, W1) ---
  const [boot, setBoot] = useState(0);
  // T204-INT: the document's own capabilities start the session as the LAST
  // GOOD ones (same S-W-R rule as a re-bootstrap): the live request below
  // still fires and replaces them, and a registry-version difference runs the
  // existing `referenceChanged` purge. Pure read here; consumed in an effect.
  const [embeddedCaps] = useState(embeddedStart);
  const [caps, setCaps] = useState<TerritoryCapabilities | null>(embeddedCaps ?? null);
  const [capsStatus, setCapsStatus] = useState<CapsStatus>(embeddedCaps ? 'loaded' : 'loading');
  // T016-FE: has this session ever seen the activated epoch (ready:true, or a
  // 426 from any discovery request)? Once true, a capabilities fault becomes
  // the explicit maintenance state — NEVER legacy (plan l.71, T010 W1).
  const [activationSeen, setActivationSeen] = useState(embeddedCaps !== undefined);
  // T016-FE: a SECOND 426 after renegotiation — the upgrade state is confirmed
  // and sticky until retry()/reload, so a hopeless bootstrap cannot loop.
  const [upgradeConfirmed, setUpgradeConfirmed] = useState(false);
  // Audit M2: browser connectivity as REACTIVE state, kept in step by the
  // `online`/`offline` listeners below. `computeGate`'s offline arm and the
  // bootstrap's skip both read this instead of the ambient `navigator.onLine`,
  // so a reconnect is reflected in the same commit that learns about it.
  const [online, setOnline] = useState<boolean>(() =>
    typeof navigator === 'undefined' ? true : navigator.onLine
  );
  const renegotiatedRef = useRef(false);
  const lastRenegotiateRef = useRef(0);
  // Cache delle richieste di contenuto normalizzate, separata dalla mappa:
  // dichiarata qui perché il cambio di registro la svuota (T016).
  // T204-INT: seeded with the resolution embedded in the document, under the
  // key the lookup below computes (`${registryVersion}:${request}`). The
  // entry is only ever hit by an identical request; a different registry
  // version in the live capabilities clears it with the rest.
  const [initialResolutions] = useState(() => {
    const seeded = new Map<string, TerritoryResolution | null>();
    const territory = embeddedCaps ? peekSsrTerritory() : undefined;
    if (embeddedCaps && territory && isTerritoryResolution(territory.body)) {
      seeded.set(`${embeddedCaps.activeRegistryVersion}:${territory.request}`, territory.body);
    }
    return seeded;
  });
  const cacheRef = useRef(initialResolutions);
  // One-shot: whatever happens next (navigation, remount) never re-reads them.
  useEffect(() => {
    consumeSsrStart();
  }, []);
  const capsRef = useRef<TerritoryCapabilities | null>(null);
  capsRef.current = caps;

  // One bounded renegotiation per 426 signal: a later 426 confirms the state.
  const reportUpgradeRequired = useCallback(() => {
    discardSsrBootstrap(); // a 426 means the epoch moved: nothing embedded is trustworthy
    setActivationSeen(true);
    if (!renegotiatedRef.current) {
      renegotiatedRef.current = true;
      setBoot((b) => b + 1);
      return;
    }
    setUpgradeConfirmed(true);
  }, []);

  useEffect(() => {
    // T016-FE (plan l.74): renegotiate on returning visible/online — this is
    // how a tab left open across the T018 activation learns the epoch moved.
    // While the browser is OFFLINE the bootstrap is skipped entirely: a failed
    // fetch must not demote a live v2 session to the maintenance state, and a
    // cold offline start resolves the gate to `legacy` in `computeGate` (see
    // the offline arm there), so the HEAD-shaped requests fire and the
    // worker's api-cache can serve them.
    const renegotiate = () => {
      if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
      const now = Date.now();
      if (now - lastRenegotiateRef.current < RENEGOTIATE_THROTTLE_MS) return;
      lastRenegotiateRef.current = now;
      setBoot((b) => b + 1);
    };
    // Audit M2: the connectivity listeners are deliberately NOT throttled — a
    // dropped `online` event must never leave the gate answering `legacy`
    // while the browser is online (and re-keying the bootstrap effect below
    // schedules the skipped round-trip even when `renegotiate` is throttled).
    const onOnline = () => {
      setOnline(true);
      renegotiate();
    };
    const onOffline = () => setOnline(false);
    const onVisibility = () => {
      if (document.visibilityState === 'visible') renegotiate();
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  useEffect(() => {
    // Audit M2: the skip reads the REACTIVE connectivity state, so a reconnect
    // re-keys this effect even when the throttled `renegotiate` dropped the
    // `online` event — a skipped bootstrap is always re-armed by the next
    // transition, never left pending forever.
    if (!online) return undefined;
    // A re-bootstrap keeps the LAST GOOD capabilities in place (S-W-R): the
    // gate must not flash `loading`/legacy on every tab focus, and a failed
    // refresh must not demote a live v2 session. Only the FIRST bootstrap
    // shows `loading`.
    const isRefresh = capsRef.current !== null;
    const controller = new AbortController();
    let timedOut = false;
    const timer = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, CAPS_TIMEOUT_MS);
    fetch('/api/territory?mode=capabilities', { signal: controller.signal })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((body: unknown) => {
        if (controller.signal.aborted) return;
        if (!isTerritoryCapabilities(body)) {
          if (!isRefresh) setCapsStatus('error');
          return;
        }
        // Reference-change detection (plan l.74): a NEW registry version means
        // every public snapshot and module cache from the old epoch is
        // mismatched — evict them before anything can serve the old bodies.
        const previous = capsRef.current;
        const referenceChanged =
          previous !== null && previous.activeRegistryVersion !== body.activeRegistryVersion;
        if (referenceChanged) {
          discardSsrBootstrap(); // gallery/feed built for the old epoch must not be served
          cacheRef.current.clear();
          resetRegistryCache();
          void purgePublicApiSnapshots();
          // A real epoch move re-arms the bounded renegotiation: the next 426
          // (should one still land) gets one fresh attempt before confirmation.
          // Deliberately NOT re-armed on an identical caps body — a server that
          // keeps 426ing compatible caps must terminate in the confirmed
          // upgrade state, not loop (T016).
          renegotiatedRef.current = false;
        }
        if (body.ready) setActivationSeen(true);
        setCaps(body);
        setCapsStatus('loaded');
      })
      .catch(() => {
        // Unmount abort stays silent; a timeout or network fault is an error.
        // With last-good caps in hand the refresh failure is absorbed (keep
        // serving the previous epoch state); without caps it maps to
        // legacy (compatible window) or maintenance (activation seen).
        if (controller.signal.aborted && !timedOut) return;
        if (!isRefresh) setCapsStatus('error');
      })
      .finally(() => clearTimeout(timer));
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [boot, online]);

  const { mode, upgradeReason } = useMemo(
    () => computeGate(caps, capsStatus, activationSeen, upgradeConfirmed, online),
    [caps, capsStatus, activationSeen, upgradeConfirmed, online]
  );
  const registryVersion = caps?.activeRegistryVersion ?? 'pre-cutover';

  // T011-FE: publish the gate for `UserProvider`, which MOUNTS ABOVE this
  // provider (it owns the saved intent we read) and therefore cannot consume
  // `mode` from context. Same computed value, same single capabilities fetch —
  // the signal just lets the preference owner retire the v1 protocol and open
  // the v2 store the moment the gate does.
  useEffect(() => {
    publishDiscoveryMode(mode);
  }, [mode]);

  // --- normalization of the primary (background) URL ---
  const normalization: DiscoveryNormalization = useMemo(
    () => normalizeDiscoveryRequest({ pathname: primary.pathname, search: primary.search }),
    [primary.pathname, primary.search]
  );
  const descriptor = useMemo(() => {
    if (normalization.kind === 'canonical' || normalization.kind === 'redirect') {
      return normalization.descriptor;
    }
    if (normalization.kind === 'needs-resolution') return normalization.pending;
    return null;
  }, [normalization]);

  // --- one-hop replace for a non-canonical URL, v2 only, foreground-safe (B3) ---
  useEffect(() => {
    if (mode !== 'v2') return;
    if (!foregroundIsPrimary) return; // a modal/overlay owns the foreground — defer
    if (normalization.kind === 'redirect') navigate(normalization.target, { replace: true });
  }, [mode, normalization, foregroundIsPrimary, navigate]);

  // --- territorial resolution (v2), cached per (registryVersion, path) ---
  // (cacheRef is declared with the capabilities block above — a reference
  // change evicts it.)
  const visitPathRef = useRef<string | null>(null);
  const explicitNationalRef = useRef(false);
  // Reactive twin of the ref, so `activeLocation` recomputes to a national
  // `{type:'none'}` after explicitItalia (W-C: the feed must go nationwide, not
  // fall back to the saved choice). The ref guards the entry effect without
  // re-keying it; this state drives the activeLocation memo.
  const [nationalCommitment, setNationalCommitment] = useState(false);
  const resolutionRunRef = useRef(0); // generation guard — W2 (explicitItalia)
  const [resolution, setResolution] = useState<TerritoryResolution | null>(null);
  const [resolutionPending, setResolutionPending] = useState(false);
  const [status, setStatus] = useState<DiscoveryStatus>('idle');
  // Key of the resolution currently reflected in `resolution`/`status`. The
  // render-time pending arm (W4): while a territorial v2 URL's key has not been
  // applied yet, `pending` is true FROM THE FIRST RENDER of the v2 transition,
  // so children (EventsContext) never fire an interim fetch before the effect
  // that would gate them has run.
  const [appliedKey, setAppliedKey] = useState<string | null>(null);

  // Il codec possiede percorso e serializzazione; soltanto i vincoli della
  // lista entrano nella richiesta. Mappa, tracking e parametri sconosciuti
  // restano nel descriptor completo senza invalidare questi stessi dati.
  // La stringa è calcolata da `@buzzmap/shared`: lo stesso codice del server
  // che incorpora la risoluzione nel documento (T204-INT).
  const territoryRequest = useMemo(
    () => (descriptor ? territoryRequestOf(descriptor) : null),
    [descriptor]
  );
  const territoryBase =
    descriptor && 'segments' in descriptor.route
      ? `/eventi/${descriptor.route.segments.join('/')}`
      : null;
  const cacheKey = territoryRequest !== null ? `${registryVersion}:${territoryRequest}` : null;

  const cacheResolution = useCallback((key: string, body: TerritoryResolution | null) => {
    cacheRef.current.delete(key);
    cacheRef.current.set(key, body);
    if (cacheRef.current.size > RESOLUTION_CACHE_LIMIT) {
      const oldest = cacheRef.current.keys().next().value;
      if (oldest !== undefined) cacheRef.current.delete(oldest);
    }
  }, []);
  const needsResolution = mode === 'v2' && cacheKey !== null && appliedKey !== cacheKey;

  // Refs mirroring render-time values for stable callbacks (retry / explicitItalia).
  const cacheKeyRef = useRef<string | null>(null);
  cacheKeyRef.current = cacheKey;
  const registryVersionRef = useRef(registryVersion);
  registryVersionRef.current = registryVersion;

  useEffect(() => {
    if (mode !== 'v2' || territoryRequest === null || cacheKey === null) {
      setResolution(null);
      setResolutionPending(false);
      setStatus('idle');
      setAppliedKey(null);
      return undefined;
    }
    const key = cacheKey;
    const applyVisitedBase = () => {
      visitPathRef.current = territoryBase;
      explicitNationalRef.current = false;
      setNationalCommitment(false);
    };
    if (cacheRef.current.has(key)) {
      const cached = cacheRef.current.get(key) ?? null;
      cacheResolution(key, cached);
      if (cached) applyVisitedBase();
      setResolution(cached);
      setResolutionPending(false);
      setStatus(cached ? 'ready' : 'unavailable');
      setAppliedKey(key);
      return undefined;
    }
    const runId = ++resolutionRunRef.current;
    setResolution(null);
    setResolutionPending(true);
    setStatus('pending');
    const controller = new AbortController();

    // Apply a settled outcome for THIS run — a stale or aborted run applies nothing.
    const applyOutcome = (outcome: ResolutionOutcome) => {
      if (controller.signal.aborted || runId !== resolutionRunRef.current) return;
      if (outcome.kind === 'http') {
        // T016-FE: a 426 means the epoch moved under this tab — renegotiate
        // (one bounded attempt) instead of treating it as a plain miss.
        if (outcome.status === 426) reportUpgradeRequired();
        // Cache only DEFINITIVE 4xx misses (a real "no such territory"). 426 is
        // a negotiation failure and 5xx is a transient fault — neither is cached,
        // so retry()/renegotiation can recover (W5, S-a).
        const definitiveMiss =
          outcome.status >= 400 && outcome.status < 500 && outcome.status !== 426;
        if (definitiveMiss) cacheResolution(key, null);
        setResolution(null);
        setStatus('unavailable');
        return;
      }
      if (!isTerritoryResolution(outcome.body)) {
        cacheResolution(key, null);
        setResolution(null);
        setStatus('unavailable');
        return;
      }
      cacheResolution(key, outcome.body);
      setResolution(outcome.body);
      setStatus('ready');
      applyVisitedBase();
    };

    fetch(`/api/territory?${territoryRequest}`, {
      signal: controller.signal,
      // B1: every v2 discovery request declares the epoch metadata; without it
      // the activated server answers 426 before any geographic query.
      headers: buildDiscoveryRequestHeaders(caps),
    })
      .then(async (r): Promise<ResolutionOutcome> => {
        if (!r.ok) return { kind: 'http', status: r.status };
        const body: unknown = await r.json();
        return { kind: 'ok', body };
      })
      .then(applyOutcome)
      .catch((err: unknown) => {
        if (runId !== resolutionRunRef.current) return;
        if (err instanceof DOMException && err.name === 'AbortError') return;
        // Network error: deliberately NOT cached — a transient outage must not
        // pin the territory as unavailable until a full reload (W5). retry()
        // re-runs the effect and finds an empty cache entry.
        setResolution(null);
        setStatus('unavailable');
      })
      .finally(() => {
        if (controller.signal.aborted || runId !== resolutionRunRef.current) return;
        setResolutionPending(false);
        setAppliedKey(key);
      });
    return () => controller.abort();
  }, [
    mode,
    territoryRequest,
    territoryBase,
    cacheKey,
    boot,
    caps,
    reportUpgradeRequired,
    cacheResolution,
  ]);

  // --- central generic-entry replacement + legacy-hint resolution (v2 only) ---
  const [entryTarget, setEntryTarget] = useState<string | null>(null);
  const [entryPending, setEntryPending] = useState(false);
  const [entrySettledKey, setEntrySettledKey] = useState<string | null>(null);
  const [hintStatus, setHintStatus] = useState<DiscoveryHintStatus>('none');
  const [hintCandidates, setHintCandidates] = useState<ResolvedMunicipality[]>([]);
  const entryRunRef = useRef(0);

  const routeKind = descriptor?.route.kind;
  const isGenericEntry = routeKind === 'generic-home' || routeKind === 'generic-esplora';
  // Saved intent (T011-FE): the v2 record, never the v1 shape. `null` while the
  // preference owner has not hydrated the v2 store yet — the entry stays HELD
  // below instead of deriving geography from a v1 store (plan l.74). The
  // primitives below (not the object itself) are the effect inputs, so a
  // consumer that recreates the record every render cannot re-key the effect.
  const savedLocationKey = JSON.stringify(preferencesV2);
  const savedHydrated = preferencesV2 != null;
  const savedComuneId =
    preferencesV2?.location.type === 'comune' ? preferencesV2.location.municipalityId : null;
  const savedRadiusKm = preferencesV2?.location.type === 'comune' ? preferencesV2.radiusKm : null;
  const hintKey =
    normalization.kind === 'needs-resolution' ? JSON.stringify(normalization.hint) : '';
  // Identity of the entry decision currently in force. Render-armed (W4): until
  // the effect has settled THIS identity, `entryHeld` keeps the feed from firing
  // an interim (saved-scoped or nationwide) query during the loading→v2 flip.
  const entryKey =
    mode === 'v2' && isGenericEntry
      ? `${primary.pathname}${primary.search}|${savedLocationKey}|${normalization.kind}|${hintKey}`
      : null;
  const entryKeyRef = useRef<string | null>(null);
  entryKeyRef.current = entryKey;
  const entryHeld = entryKey !== null && entrySettledKey !== entryKey;

  useEffect(() => {
    if (entryKey === null) {
      setEntryTarget(null);
      setEntryPending(false);
      setHintStatus('none');
      setHintCandidates([]);
      return;
    }
    if (!savedHydrated) {
      // T011-FE: the v2 store is not hydrated/converted yet (the gate has just
      // opened, or a v1→v2 conversion is in flight). Stay unsettled so
      // `entryHeld` keeps the feed closed — never derive geography from the v1
      // store in v2 mode, and never fire an interim broader query (plan l.74).
      setEntryTarget(null);
      setEntryPending(false);
      return;
    }
    if (explicitNationalRef.current) {
      // Explicit Italia: the decision is "national" — release the hold and do
      // not replace until the user explicitly visits a territory again.
      setEntryTarget(null);
      setEntryPending(false);
      setEntrySettledKey(entryKey);
      return;
    }
    if (!foregroundIsPrimary) {
      // B3: a modal/auth overlay owns the foreground. Defer the background
      // normalization — do NOT mark settled (the decision re-runs when the
      // foreground returns) and never move the overlay.
      setEntryTarget(null);
      setEntryPending(false);
      return;
    }
    if (!descriptor) {
      setEntryTarget(null);
      setEntryPending(false);
      setEntrySettledKey(entryKey);
      return;
    }

    const runId = ++entryRunRef.current;
    const entry = descriptor.route.kind === 'generic-home' ? 'home' : 'esplora';
    const currentUrl = `${primary.pathname}${primary.search}`;

    const settle = () => {
      if (runId !== entryRunRef.current) return;
      setEntryPending(false);
      setEntrySettledKey(entryKey);
    };
    const failHint = () => {
      if (runId !== entryRunRef.current) return;
      setEntryTarget(null);
      setHintStatus('invalid');
      settle();
    };
    const applyPath = (comunePath: string | null, carrySearch: string) => {
      if (runId !== entryRunRef.current) return;
      if (!comunePath) {
        setEntryTarget(null);
        return;
      }
      const target = composeTerritoryTarget(
        entry,
        comunePath,
        carrySearch,
        // The radius is an independent v2 preference: it is materialized only
        // when a comune is the saved choice — a `none`/needs-confirmation
        // radius is retained but never applied as a geographic filter
        // (plan l.69), which is exactly the pre-T011 `radiusOfSaved(none)` = no
        // radius behaviour.
        savedRadiusKm,
        COMUNE_PRESET_RADIUS_KM
      );
      setEntryTarget(target !== currentUrl ? target : null);
    };

    // B-1: only a BARE generic entry (or a geo-hint entry) may be replaced. A
    // free-map URL is an explicit spatial mode (plan l.65-66) and is never
    // replaced; a filtered generic entry keeps its explicit filters (T012 owns
    // territorial filters) — the saved/visit geography must not stomp them.
    // W-R3-2 (T013): a GEO-HINT entry resolves regardless of free-map state —
    // `?city=Roma&bbox=…` normalizes to a pending descriptor that already
    // carries view=map+mapBbox, and skipping hint resolution there would drop
    // the hint and settle to saved/nationwide (plan l.61 forbids it). The hint
    // carry already serializes the free-map state via `canonicalSearch`, so
    // the replacement honors both the geography and the explicit map mode.
    const isHint = normalization.kind === 'needs-resolution';
    if (
      !isHint &&
      (descriptor.view === 'map' || descriptor.mapBbox !== null || hasNonGeoState(descriptor))
    ) {
      setEntryTarget(null);
      setHintStatus('none');
      setHintCandidates([]);
      settle();
      return;
    }
    // Remaining query state an eligible replacement must carry (B-1, W-R3-1).
    // For a needs-resolution entry `descriptor` IS the pending descriptor; for
    // a BARE entry it carries at least the tracking/unknown params (and an
    // explicit `radiusKm`), which plan l.60 requires to survive navigation. A
    // truly bare descriptor yields '' so the shared builder owns that case.
    const carrySearch = descriptor.canonicalSearch;

    // Apply a pair-hint registry result: several matches → manual choice, one
    // pathed match → replacement, anything else → invalid (never a silent guess).
    const applyPairMatches = (matches: RegistryCityItem[]) => {
      if (matches.length > 1) {
        // Homonym without a discriminating province — manual choice, never
        // the first row (plan l.61 / R2).
        setEntryTarget(null);
        setHintStatus('needs-selection');
        setHintCandidates(matches.map(toResolvedMunicipality));
      } else if (matches[0]?.path) {
        applyPath(matches[0].path, carrySearch);
        setHintStatus('none');
      } else {
        setEntryTarget(null);
        setHintStatus('invalid');
      }
      settle();
    };

    // B2: an explicit legacy geographic hint wins over the visit fallback and
    // the saved choice — it is the user's stated intent for THIS entry.
    const resolveHint = (hint: GeographicHint) => {
      setHintStatus('resolving');
      setHintCandidates([]);
      setEntryPending(true);
      if (hint.kind === 'pair') {
        loadRegistry()
          .then((reg) => {
            if (runId !== entryRunRef.current) return;
            applyPairMatches(matchRegistryPair(reg.cities ?? [], hint));
          })
          .catch(() => failHint());
        return;
      }
      // Center hint: one-time point containment through the T005 endpoint.
      resolvePoint(hint.lat, hint.lng)
        .then((result) => {
          if (runId !== entryRunRef.current) return;
          if (result.kind === 'resolved' && result.municipality.path) {
            applyPath(result.municipality.path, carrySearch);
            setHintStatus('none');
          } else if (result.kind === 'ambiguous') {
            setEntryTarget(null);
            setHintStatus('needs-selection');
            setHintCandidates(result.candidates);
          } else {
            setEntryTarget(null);
            setHintStatus('invalid');
          }
          settle();
        })
        .catch(() => failHint());
    };

    if (isHint) {
      resolveHint(normalization.hint);
      return;
    }

    setHintStatus('none');
    setHintCandidates([]);

    if (visitPathRef.current) {
      applyPath(visitPathRef.current, carrySearch);
      settle();
      return;
    }

    // Saved-choice replacement (T011-FE / S2 handoff): the v2 record carries a
    // municipality UUID, so the path comes from the REGISTRY by id — no
    // coordinates, no containment POST, no nearest name.
    if (savedComuneId) {
      setEntryPending(true);
      findRegistryById(savedComuneId)
        .then((row) => {
          if (runId !== entryRunRef.current) return;
          // An id the current registry does not know (stale record): no
          // replacement — the entry settles nationally instead of guessing.
          applyPath(row?.path ?? null, carrySearch);
          settle();
        })
        .catch(() => {
          if (runId !== entryRunRef.current) return;
          setEntryTarget(null);
          settle();
        });
      return;
    }

    setEntryTarget(null);
    settle();
    // The v2 saved intent is produced once per hydration/conversion in
    // UserContext (stable primitives, see above), so the derived values are
    // safe as dependencies.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    entryKey,
    foregroundIsPrimary,
    descriptor,
    normalization,
    primary.pathname,
    primary.search,
    savedHydrated,
    savedComuneId,
    savedRadiusKm,
  ]);

  // Apply the entry replacement as a SPA replace — foreground-safe (B3): never
  // navigate while a modal/overlay owns the foreground; the effect re-runs when
  // the foreground returns and then applies.
  useEffect(() => {
    if (mode === 'v2' && entryTarget && foregroundIsPrimary) {
      navigate(entryTarget, { replace: true });
    }
  }, [mode, entryTarget, foregroundIsPrimary, navigate]);

  // --- derived state ---
  const hintHeld =
    hintStatus === 'resolving' || hintStatus === 'needs-selection' || hintStatus === 'invalid';
  // W-B: an invalid route/filter normalization is a held v2 state — the feed must
  // not fall back to stored geography under a bad URL (plan l.60; T016 renders
  // the invalid + retry UI on top of this hold).
  // D-T017-B-1: `not-a-discovery-path` is NOT an invalid discovery URL — it is
  // EVERY non-discovery route (/profilo, /salvati, /accedi, /privacy, …) where
  // no filter exists and nothing failed. Only genuine discovery-surface
  // failures (malformed /eventi/* shapes, invalid filters) hold the feed and
  // show the "filtri non validi" notice.
  const invalidHeld =
    mode === 'v2' &&
    (normalization.kind === 'invalid-filter' ||
      (normalization.kind === 'invalid-route' && normalization.reason !== 'not-a-discovery-path'));
  // Render-armed (W4): `needsResolution`/`entryHeld` are computed during render,
  // so the gate is closed on the very render where mode flips to v2 — before any
  // effect could fire an interim events query.
  const pending =
    mode === 'v2' &&
    (needsResolution || resolutionPending || entryHeld || entryPending || hintHeld || invalidHeld);
  const settled = mode !== 'v2' || !pending;
  // La pertinenza è verificata durante il render, prima che l'effetto possa
  // cancellare lo stato precedente: nessun ready o vetrina vecchi sotto un URL nuovo.
  const currentResolution =
    mode === 'v2' && cacheKey !== null && appliedKey === cacheKey && !invalidHeld
      ? resolution
      : null;
  const activeLocation = useMemo<LocationChoice | null>(() => {
    if (mode !== 'v2') return null;
    // W-C: after explicit Italia the feed goes NATIONWIDE — publish a national
    // `{type:'none'}` so the bridge does not substitute the saved choice.
    if (nationalCommitment) return { type: 'none' };
    if (!currentResolution) return null;
    const activeRadius = activeRadiusKm(currentResolution, descriptor?.filters.radiusKm);
    return { ...currentResolution.location, radiusKm: activeRadius };
  }, [mode, currentResolution, descriptor, nationalCommitment]);

  const explicitItalia = useCallback(() => {
    explicitNationalRef.current = true;
    setNationalCommitment(true); // W-C: drives the national activeLocation
    visitPathRef.current = null;
    entryRunRef.current++;
    resolutionRunRef.current++; // W2: invalidate an in-flight resolution — a late
    // response can no longer resurrect the territory or erase the commitment.
    setResolution(null);
    setStatus('idle');
    setResolutionPending(false);
    // Mark the current territorial key as applied (with no resolution) so the
    // render-time arm does not hold the feed forever on a still-territorial URL.
    setAppliedKey(cacheKeyRef.current);
    setEntryTarget(null);
    setEntryPending(false);
    setHintStatus('none');
    setHintCandidates([]);
    setEntrySettledKey(entryKeyRef.current);
  }, []);

  const retry = useCallback(() => {
    // W5: drop the current territory's cache entry so a settled network error
    // (deliberately uncached) or a cached 404 can actually be retried.
    if (cacheKeyRef.current) cacheRef.current.delete(cacheKeyRef.current);
    // T016-FE: a manual retry also releases the confirmed upgrade state and
    // re-arms the bounded renegotiation, so a transient fault that put the
    // client on the upgrade screen can recover without a hard reload.
    renegotiatedRef.current = false;
    setUpgradeConfirmed(false);
    setBoot((b) => b + 1);
  }, []);

  // W-B: surface an invalid normalization outcome as a held `invalid` status so
  // the bridge keeps the feed closed under a bad URL.
  let effectiveStatus: DiscoveryStatus = status;
  if (invalidHeld) {
    effectiveStatus = 'invalid';
  } else if (needsResolution) {
    effectiveStatus = 'pending';
  } else if (mode === 'v2' && cacheKey === null) {
    effectiveStatus = 'idle';
  }

  const value = useMemo<DiscoveryContextType>(
    () => ({
      mode,
      capabilities: caps,
      descriptor,
      status: effectiveStatus,
      resolution: currentResolution,
      settled,
      activeLocation,
      pending,
      entryReplacementTarget: entryTarget,
      hintStatus,
      hintCandidates,
      upgradeReason,
      explicitItalia,
      retry,
      reportUpgradeRequired,
    }),
    [
      mode,
      caps,
      descriptor,
      effectiveStatus,
      currentResolution,
      settled,
      activeLocation,
      pending,
      entryTarget,
      hintStatus,
      hintCandidates,
      upgradeReason,
      explicitItalia,
      retry,
      reportUpgradeRequired,
    ]
  );

  return (
    <DiscoveryContext.Provider value={value}>
      {children}
      {/* T013: the needs-selection prompt for an ambiguous legacy hint —
          rendered here so it cannot drift from the held hint state it
          belongs to (v2-only, public tree only). */}
      <HintSelectionPrompt />
      {/* T016-FE: the upgrade/maintenance blocking state + the invalid /
          unavailable territory notices — mounted here so they cover EVERY
          public surface (LocalitaPopup, profile, creator forms, tabs fall
          back to legacy literals only in non-v2 modes, plan l.71). */}
      <DiscoveryEpochNotice />
      {/* T016-FE: the foreground route owns title/canonical/robots/structured
          data; background resolution never overwrites event metadata. */}
      <RouteMetadata />
    </DiscoveryContext.Provider>
  );
}
